import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Col, Row, Collapse, CardTitle, Button, Spinner } from 'reactstrap';
import * as utils from '../../Services/utils';
import { useForm } from 'react-hook-form';
import FormInput from '../ReactHookFormCustom/FormInput';
import FormSelect from '../ReactHookFormCustom/FormSelect';
import FormDateTime from '../ReactHookFormCustom/FormDateTime';
import FormCheckBox from '../ReactHookFormCustom/FormCheckBox';
import { dateFormat } from '../ReactHookFormCustom/FormHelpers';
import FormLocationFull from '../ReactHookFormCustom/FormLocationFull';
import FormRichTextBox from '../ReactHookFormCustom/FormRichTextBox';
import { getCountries } from '../../Services/countries';
import { updateJob } from '../../Services/jobService';
import { createJob } from '../../Services/jobService';
import swal from 'sweetalert';
import { useHistory } from 'react-router-dom';
import { emailFormat } from '../ReactHookFormCustom/FormHelpers';

const JobForm = ({ job, refreshJob, isOpen, toggleAccordion, getAccordionIcon }) => {
  const { t, i18n } = useTranslation();
  const { handleSubmit, reset, control, setValue } = useForm({});
  const history = useHistory();

  const countries = getCountries(i18n.language);
  const jobFields = utils.getJobFieldList();
  const workLocations = utils.getJobLocationType();
  const experienceTypes = utils.getExperienceType();
  const jobTypes = utils.getJobTypeList();
  const currencies = utils.getCurrencyList();
  const employmentTypes = utils.getEmploymentType();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (job.address) {
      const countryOption = countries.find((c) => c.value.toLowerCase() === job.address?.countryCode.toLowerCase());
      const cityOption = { label: job.address?.city, value: job.address?.city }; // Assuming this is acceptable
      const streetOption = { label: job.address?.street, value: job.address?.street };
      const jobFieldOption = jobFields.find((jobField) => jobField.value === job.field);
      const workLocationOption = workLocations.find((workLocation) => workLocation.value === job.jobLocationType);
      const experienceOption = experienceTypes.find((experienceType) => experienceType.value === job.experience);
      const jobTypeOption = jobTypes.find((jobType) => jobType.value === job.jobType);
      const currencyOption = currencies.find((currency) => currency.value === job.currency);
      const employmentTypeOption = employmentTypes.find(
        (employmentType) => employmentType.value === job.employmentType
      );

      // This sets values to the form objects.
      reset({
        ...job,
        countryControllerSelect: countryOption,
        cityControllerSelect: cityOption,
        streetControllerSelect: streetOption,

        field: jobFieldOption,
        jobLocationType: workLocationOption,
        experience: experienceOption,
        jobTitle: job.title,
        jobDescription: job.description,
        expirationDate: job.expirationDate ? new Date(job.expirationDate) : null,
        jobType: jobTypeOption,
        currency: currencyOption,
        employmentType: employmentTypeOption,
      });
    } else {
      // 'Timepicker' is exception
      reset({
        expirationDate: new Date().setMonth(new Date().getMonth() + 2),
      });
    }
  }, [job]);

  const handleSubmitWithValidations = async (values) => {
    await saveOrUpdate(values);
  };

  const saveOrUpdate = async (valuesForJob) => {
    if (isLoading) {
      return;
    }
    if (!valuesForJob.location) {
      swal({ icon: 'warning', closeOnClickOutside: false, title: t('jobs.LOCATION_IS_NULL') });
      return;
    }

    setIsLoading(true);

    const jobData = {
      ...valuesForJob,
      address: {
        countryCode: valuesForJob.countryControllerSelect?.value,
        city: valuesForJob.cityControllerSelect?.label,
        street: valuesForJob.streetControllerSelect?.label,
        googlePlaceId: valuesForJob.location?.googlePlaceId,
        lat: valuesForJob.location?.lat,
        lng: valuesForJob.location?.lng,
        latForStreetSearch: valuesForJob.location?.latForStreetSearch,
        lngForStreetSearch: valuesForJob.location?.lngForStreetSearch,
      },
      currency: valuesForJob.currency?.value,
      field: valuesForJob.field?.value,
      jobLocationType: valuesForJob.jobLocationType?.value,
      experience: valuesForJob.experience?.value,
      title: valuesForJob.jobTitle,
      description: valuesForJob.jobDescription,
      employmentType: valuesForJob.employmentType?.value,
      jobType: valuesForJob.jobType?.value,
      expirationDate: new Date(valuesForJob.expirationDate),
    };
    if (job.id) {
      updateJob(jobData)
        .then((response) => {
          refreshJob(response.data);
          history.push(`/jobs/${job.id}`);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      createJob(jobData)
        .then((response) => {
          refreshJob(response.data.jobToReturn);
          history.push(`/jobs/${response.data.jobToReturn.id}`);
          swal({ icon: 'success', title: t('jobs.NEW_JOB_CREATED') });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Row className="justify-content-center">
      <Col md={10}>
        <Card className="card-default mb-1">
          <CardHeader style={{ cursor: 'pointer' }} onClick={toggleAccordion}>
            <CardTitle tag="h1">
              <h3>
                <small>
                  <em className={getAccordionIcon()}></em>
                </small>
                {t('jobs.JOB_DEFINITION')}
              </h3>
            </CardTitle>
          </CardHeader>
          <Collapse isOpen={isOpen}>
            <div className="col-md-12">
              <form onSubmit={handleSubmit(handleSubmitWithValidations)}>
                <Row className="pb-3">
                  <Col lg={4}>
                    <FormInput
                      control={control}
                      name="jobTitle"
                      id="jobTitleId"
                      label={t('jobs.JOB_TITLE')}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormSelect
                      control={control}
                      name="field"
                      id="fieldId"
                      label={t('applications.JOB_FIELD')}
                      options={jobFields}
                      defaultValue={jobFields[0]}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormSelect
                      control={control}
                      name="jobLocationType"
                      id="jobLocationTyped"
                      label={t('jobs.JOB_WORK_LOCATION_TYPE')}
                      options={workLocations}
                      defaultValue={workLocations[0]}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                </Row>
                <Row className="pb-3">
                  <Col lg={4}>
                    <FormInput
                      control={control}
                      name="language"
                      id="languageId"
                      label={t('common.LANGUAGE')}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormDateTime
                      control={control}
                      label={t('jobs.EXPIRATION_DATE')}
                      name="expirationDate"
                      id="expirationDateId"
                      timeFormat={false}
                      rules={{
                        required: t('common.REQUIRED'),
                        pattern: {
                          value: dateFormat,
                          message: t('settings.DATE_FORMAT_HELP'),
                        },
                      }}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormSelect
                      control={control}
                      name="experience"
                      id="experienceId"
                      label={t('jobs.EXPERIENCE')}
                      options={experienceTypes}
                      defaultValue={experienceTypes[0]}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                </Row>
                <Row className="pb-3">
                  <Col lg={4}>
                    <FormSelect
                      control={control}
                      name="jobType"
                      id="jobTypeId"
                      label={t('applications.JOB_TYPE')}
                      options={jobTypes}
                      defaultValue={jobTypes[0]}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormSelect
                      control={control}
                      name="employmentType"
                      id="employmentTypeId"
                      label={t('jobs.EMPLOYMENT_TYPE')}
                      options={employmentTypes}
                      defaultValue={employmentTypes[0]}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                  <Col lg={4}></Col>
                </Row>
                <Row className="pb-3">
                  <Col lg={4}>
                    <FormSelect
                      control={control}
                      name="currency"
                      id="currencyId"
                      label={t('common.CURRENCY')}
                      options={currencies}
                      // defaultValue={utils.getCurrencyList()[0].value}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormInput
                      control={control}
                      name="minSalary"
                      id="minSalaryId"
                      type="number"
                      label={t('common.MIN_SALARY')}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormInput
                      control={control}
                      name="maxSalary"
                      id="maxSalaryId"
                      type="number"
                      label={t('common.MAX_SALARY')}
                    />
                  </Col>
                </Row>
                <Row className="pb-3">
                  <FormLocationFull control={control} setValue={setValue} address={job.address} />
                </Row>
                <Row className="pb-3">
                  <Col>
                    <FormCheckBox
                      name="isCvMandatory"
                      id="isCvMandatoryId"
                      control={control}
                      label={t('jobs.CV_MANDATORY')}
                    />
                  </Col>
                </Row>
                <Row className="pb-3">
                  <Col>
                    <FormRichTextBox
                      control={control}
                      name="jobDescription"
                      id="jobDescriptionId"
                      label={t('jobs.JOB_DESCRIPTION')}
                      rules={{
                        required: t('common.REQUIRED'),
                      }}
                    />
                  </Col>
                </Row>

                <Row className="pt-5">
                  <Col lg={4}>
                    <FormInput
                      control={control}
                      name="email1ForNotifications"
                      id="email1ForNotificationsId"
                      label={t('jobs.EMAIL_FOR_NOTIFICATIONS')}
                      rules={{
                        pattern: {
                          value: emailFormat,
                          message: t('settings.EMAIL_HELP'),
                        },
                      }}
                    />
                  </Col>
                  <Col lg={4}>
                    <FormInput
                      control={control}
                      name="email2ForNotifications"
                      id="email2ForNotificationsId"
                      label={t('jobs.EMAIL2_FOR_NOTIFICATIONS')}
                      rules={{
                        pattern: {
                          value: emailFormat,
                          message: t('settings.EMAIL_HELP'),
                        },
                      }}
                    />
                  </Col>
                </Row>
                <Row className="pb-3">
                  <Col className="d-flex justify-content-end">
                    <Button type="submit" color="primary" disabled={isLoading}>
                      {isLoading && <div className="spinner-border spinner-border-sm" role="status" />}
                      &nbsp;{t('common.SAVE')}
                    </Button>
                  </Col>
                </Row>
              </form>
            </div>
          </Collapse>
        </Card>
      </Col>
    </Row>
  );
};

export default JobForm;
